// A backup of everything this phone knows, as one file the lifter keeps.
//
// WHY A FILE AND NOT AN ACCOUNT. Work Up has no server copy of anybody's data,
// on purpose: no sign-in, nothing to breach, nothing to pay for. The price is
// that a lost or wiped phone is lost data — so the lifter can take the whole
// store with them as a JSON file (Files, iCloud Drive, an email to themselves)
// and put it back on this phone or a new one.
//
// WHAT IS IN IT: every record in the store (kit, plates, lifts, warm-ups,
// every set lifted) and the app's settings. WHAT IS NOT: the clips. A filmed
// set holds a POINTER into the photo library, never the video; the videos live
// in Photos and come back with the phone's own backup. On a different phone the
// pointers won't resolve, and the Film screen already says so and offers Relink.
//
// makeBackup / readBackup are pure, so the format can be tested; collect and
// restore do the reading and writing.
import { records } from '../core/storage.js';

export const BACKUP_KIND = 'work-up-backup';
export const BACKUP_VERSION = 1;
const PREF = 'pref:';

/** Counts per kind of record, for the "3 lifts, 42 sets" a person checks before restoring. */
export function countRecords(rows) {
  const by = (e) => rows.filter((r) => r.entity === e).length;
  return { lifts: by('lift'), sets: by('attempt'), plates: by('plate'), kits: by('kit') };
}

/**
 * The file's contents. Only real records (positive ids — demo rows are
 * negative), and without the store's own bookkeeping flag.
 */
export function makeBackup(rows, prefs = {}, now = new Date()) {
  const kept = rows
    .filter((r) => r && Number(r.id) > 0 && typeof r.entity === 'string' && r.entity)
    .map((r) => {
      const copy = { ...r };
      delete copy.dirty;
      return copy;
    })
    .sort((a, b) => Number(a.id) - Number(b.id));
  return {
    kind: BACKUP_KIND,
    version: BACKUP_VERSION,
    app: 'Work Up',
    exportedAt: now.toISOString(),
    counts: countRecords(kept),
    records: kept,
    prefs: { ...prefs },
  };
}

/**
 * Read a file back, or say plainly why not. Nothing here writes anything.
 * @returns {{ ok: true, backup: object, counts: object, exportedAt: string }
 *   | { ok: false, reason: 'not-json' | 'not-a-backup' | 'newer' | 'damaged' }}
 */
export function readBackup(text) {
  let data;
  try { data = JSON.parse(String(text ?? '').replace(/^\uFEFF/, '')); } catch { return { ok: false, reason: 'not-json' }; }
  if (!data || data.kind !== BACKUP_KIND) return { ok: false, reason: 'not-a-backup' };
  if (!(Number(data.version) >= 1)) return { ok: false, reason: 'damaged' };
  if (Number(data.version) > BACKUP_VERSION) return { ok: false, reason: 'newer' };
  const rows = Array.isArray(data.records) ? data.records : null;
  if (!rows) return { ok: false, reason: 'damaged' };
  const ids = new Set();
  for (const r of rows) {
    const id = Number(r?.id);
    if (!(id > 0) || !Number.isInteger(id) || typeof r.entity !== 'string' || !r.entity || ids.has(id)) {
      return { ok: false, reason: 'damaged' };
    }
    ids.add(id);
  }
  const prefs = data.prefs && typeof data.prefs === 'object' && !Array.isArray(data.prefs) ? data.prefs : {};
  const backup = { ...data, records: rows, prefs };
  return { ok: true, backup, counts: countRecords(rows), exportedAt: String(data.exportedAt ?? '') };
}

/** Why a file was refused, in the app's words. */
export const REFUSED = {
  'not-json': "That file isn't a Work Up backup.",
  'not-a-backup': "That file isn't a Work Up backup.",
  newer: 'That backup is from a newer Work Up. Update the app, then restore it.',
  damaged: "That backup is damaged and can't be restored.",
};

function readPrefs() {
  const out = {};
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREF)) out[k.slice(PREF.length)] = JSON.parse(localStorage.getItem(k));
    }
  } catch { /* private mode: no settings to keep */ }
  return out;
}

/** Everything on this phone, as a backup object. */
export async function collectBackup(now = new Date()) {
  const rows = await records.all();
  return makeBackup(rows, readPrefs(), now);
}

/** `work-up-backup-2026-09-11.json`, dated in the lifter's own day. */
export function backupFilename(now = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `work-up-backup-${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}.json`;
}

/**
 * REPLACE everything on this phone with a backup. The caller confirms first —
 * this is the one destructive action in the app — and reloads afterwards, so the
 * store rehydrates from what was written and its id sequence starts past the
 * highest restored id.
 */
export async function restoreBackup(backup) {
  const existing = await records.all();
  for (const r of existing) await records.delete(r.id);
  for (const r of backup.records) await records.put(r);
  try {
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREF)) localStorage.removeItem(k);
    }
    for (const [k, v] of Object.entries(backup.prefs ?? {})) localStorage.setItem(PREF + k, JSON.stringify(v));
    // Today's overrides and ramp positions belong to the data they were made
    // against; a restored store starts the day clean.
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const k = sessionStorage.key(i);
      if (k?.startsWith('work-up:')) sessionStorage.removeItem(k);
    }
  } catch { /* storage unavailable: the records are what matter */ }
}
